<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';

const props = defineProps<{
  affiliateName: string;
  config: AffiliateConfig;
  loading?: 'lazy' | 'eager';
  /** Quando true, o card preenche a largura da coluna (usado na página
   *  perfil-oficial). Default (false) mantém a largura fixa usada na home. */
  fluidWidth?: boolean;
}>();

const router = useRouter();

const widthClass = computed(() =>
  props.fluidWidth ? 'w-full max-w-[180px] min-w-0' : 'w-[180px]',
);

const displayName = computed(() => {
  return props.config.affiliateDetails?.name || props.affiliateName;
});

const avatarUrl = computed(() => {
  return props.config.affiliateDetails?.avatarUrl || '';
});

const badge = computed(() => {
  return props.config.affiliateDetails?.title || '';
});

const categories = computed(() => {
  return props.config.categories;
});

const CARD_ACCENT_COLOR = '#00C9FF';

const cardAccentColorRgb = computed(() => {
  const hex = CARD_ACCENT_COLOR.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r},${g},${b}`;
});

const cardAvatarGradient = computed(() => {
  const color = CARD_ACCENT_COLOR;

  return `radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, ${color} 45.19%, ${color}80 100%)`;
});

// Drag functionality variables
const categoriesContainer = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const dragged = ref(false);
const goToProfile = () => {
  router.push(`/perfil-oficial/${props.affiliateName}`);
};

// Drag functionality functions
function onMouseDown(e: MouseEvent) {
  if (!categoriesContainer.value) return;
  isDragging.value = true;
  startX.value = e.pageX - categoriesContainer.value.offsetLeft;
  scrollLeft.value = categoriesContainer.value.scrollLeft;
  categoriesContainer.value.style.cursor = 'grabbing';
  categoriesContainer.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value || !categoriesContainer.value) return;
  e.preventDefault();

  const x = e.pageX - categoriesContainer.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  categoriesContainer.value.scrollLeft = scrollLeft.value - walk * 1;
}

function onMouseUp() {
  if (!categoriesContainer.value) return;
  isDragging.value = false;
  categoriesContainer.value.style.cursor = 'grab';
  categoriesContainer.value.style.userSelect = 'auto';
  if (dragged.value) {
    setTimeout(() => {
      dragged.value = false;
    }, 50);
  }
}

function onMouseLeave() {
  if (!categoriesContainer.value) return;
  isDragging.value = false;
  categoriesContainer.value.style.cursor = 'grab';
  categoriesContainer.value.style.userSelect = 'auto';
}

function handleCategoryClick(event: MouseEvent) {
  if (dragged.value) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    dragged.value = false;
  }
}
</script>

<template>
  <div
    :class="[
      'affiliate-card flex flex-col justify-center items-center gap-2 bg-gradient-to-b from-[#08112d] to-[#0c1b44] p-4 rounded-[20px] select-none border border-solid border-[#203570]',
      widthClass,
    ]"
  >
    <div
      class="avatar-container w-24 h-24 rounded-full border-2 border-solid overflow-hidden flex-shrink-0 cursor-pointer"
      :style="{
        borderColor: CARD_ACCENT_COLOR,
        background: cardAvatarGradient,
      }"
      @click="goToProfile"
    >
      <NuxtImg
        v-if="avatarUrl"
        alt="Avatar"
        class="avatar-image object-center"
        draggable="false"
        :loading="props.loading || 'lazy'"
        :src="avatarUrl"
      />
      <div
        v-else
        class="avatar-image w-full h-full flex items-center justify-center text-white/50 text-2xl font-bold"
      >
        {{ displayName.charAt(0).toUpperCase() }}
      </div>
    </div>

    <!-- Name and Badge -->
    <div class="flex items-center justify-center gap-[6px] w-full">
      <p class="text-white text-center text-base font-extrabold leading-6">{{
        displayName
      }}</p>
      <NuxtIcon
        v-if="badge"
        class="text-primary text-[23px]"
        :filled="true"
        name="perfil-oficial/embaixador-oficial"
      />
    </div>

    <!-- View Profile Button -->
    <button
      class="flex justify-center items-center self-stretch h-8 px-3 bg-[#FF3700] rounded-xl"
      @click="goToProfile"
    >
      <span class="text-white text-xs font-normal leading-4">VER PERFIL</span>
    </button>

    <!-- Categories -->
    <div
      ref="categoriesContainer"
      class="categories-container flex gap-2 overflow-x-auto whitespace-nowrap max-w-full"
      @mousedown="onMouseDown"
      @mouseleave="onMouseLeave"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
    >
      <span
        v-for="category in categories"
        :key="category"
        class="flex justify-center items-center px-3 py-1 rounded-[100px] shrink-0"
        :style="{ background: `rgba(${cardAccentColorRgb},0.20)` }"
        @click="handleCategoryClick"
      >
        <span
          class="text-center text-xs font-medium leading-4"
          :style="{ color: CARD_ACCENT_COLOR }"
          >{{ category }}</span
        >
      </span>
    </div>
  </div>
</template>

<style scoped>
.affiliate-card {
  min-height: 200px;
}

.categories-container {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  cursor: grab;
}

.categories-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}
</style>
